import type { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import type { OptJobType } from 'api/optJobs/optJobs.api'
import { getAliasSubtypes } from 'helpers/appliedSettingValues.helper/optJobRuns.helper'
import { getSelectedPropertiesByOfferedTags } from 'helpers/appliedSettingValues.helper/propertySelection.helper'
import { OPTJOB_TYPE_NAME_TO_ID } from 'ui/uiConfig/template/aliases'
import { OUTCOME_OPTJOB_TYPE } from 'views/DigitalTwinCollectedSettingsView/components/CollectedSettingsAppliedSettingsTable/CollectedSettingsAppliedSettingsTable.helper'
import {
  getConfigurablePriorityLevels,
  getHiddenPriorityLevelSet,
} from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'

type FollowupAliasDefaultsSystem = {
  subtypes: Partial<Record<string, Subtype[]>> | undefined
  settingPriorityLevels: SystemSettingPriorityLevel[] | null | undefined
}

/** The system's configurable levels for the opt job type, only its named subtypes' when it names any; highest first. */
function getSystemPriorityLevels(
  optJobType: string | undefined,
  subtypeNames: string[],
  { subtypes, settingPriorityLevels }: FollowupAliasDefaultsSystem
): number[] {
  const typeSubtypes = (optJobType && subtypes?.[optJobType]) || []
  const namedSubtypes = subtypeNames.length
    ? typeSubtypes.filter(({ subtype }) => subtypeNames.includes(subtype))
    : typeSubtypes

  return getConfigurablePriorityLevels(namedSubtypes, getHiddenPriorityLevelSet(settingPriorityLevels)).toReversed()
}

/** The opt job type the cell names: its `optimal_optjob_type`, else its `optjob_type`. */
export function getNamedOptJobType(overrideAlias: UiConfigAliases | undefined): string | undefined {
  const alias = overrideAlias?.optimal_optjob_type ?? overrideAlias?.optjob_type

  return typeof alias === 'string' ? alias : undefined
}

/**
 * The aliases the cell resolves with. The cell names only `optjob_type`; the rest default: optimal on that opt job
 * type, outcome on `measvalues_calculations`, the opt job type ids by name, and the `priority_levels` the system
 * configures for the opt job type (or the named `optjob_subtype`s). An alias the cell sets itself wins.
 */
export function withFollowupAliasDefaults(
  overrideAlias: UiConfigAliases | undefined,
  system: FollowupAliasDefaultsSystem
): UiConfigAliases | undefined {
  if (!overrideAlias) {
    return undefined
  }

  const optimalType = getNamedOptJobType(overrideAlias)
  const outcomeAlias = overrideAlias.outcome_optjob_type
  const outcomeType = typeof outcomeAlias === 'string' ? outcomeAlias : OUTCOME_OPTJOB_TYPE
  const cellPriorityLevels = getPriorityLevels(overrideAlias)
  const withDefaults: Record<string, UiConfigAliasValue | undefined> = {
    optjob_type: optimalType,
    optimal_optjob_type: optimalType,
    outcome_optjob_type: outcomeType,
    optimal_opt_job_type_id: optimalType ? OPTJOB_TYPE_NAME_TO_ID[optimalType as OptJobType] : undefined,
    outcome_opt_job_type_id: OPTJOB_TYPE_NAME_TO_ID[outcomeType as OptJobType],
    ...overrideAlias,
    priority_levels: cellPriorityLevels.length
      ? cellPriorityLevels
      : getSystemPriorityLevels(optimalType, getAliasSubtypes(overrideAlias), system),
  }

  const aliases: UiConfigAliases = {}
  for (const [alias, value] of Object.entries(withDefaults)) {
    if (value !== undefined) {
      aliases[alias] = value
    }
  }

  return aliases
}

/** The priority levels the alias `priority_levels` lists, highest first; none when it lists anything else. */
export function getPriorityLevels(overrideAlias: UiConfigAliases | undefined): number[] {
  const priorityLevels = overrideAlias?.priority_levels

  return Array.isArray(priorityLevels) && priorityLevels.every((level) => typeof level === 'number')
    ? priorityLevels.toSorted((a, b) => b - a)
    : []
}

type FollowupSettingsSelectionInput = {
  allProperties: ConfigurableProperty[]
  priorityLevels: number[]
  filterTags: string[]
  /** The tags the header filter offers: the only categories whose tags can select a property. */
  offeredTags: string[][]
  isHeaderFiltered: boolean
}

type FollowupSettingsSelection = { selectedProperties: ConfigurableProperty[]; isFiltered: boolean }

/** The settings follow the header filter, and nothing shows until it is used. */
export function toFollowupSettingsSelection({
  allProperties,
  priorityLevels,
  filterTags,
  offeredTags,
  isHeaderFiltered,
}: FollowupSettingsSelectionInput): FollowupSettingsSelection {
  return {
    selectedProperties: getSelectedPropertiesByOfferedTags(allProperties, priorityLevels, filterTags, offeredTags),
    isFiltered: isHeaderFiltered,
  }
}
